import type { Ref } from 'react';
import { requestForQuoteProductName } from '~/utils/requestForQuoteProductName';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Button } from '~/components/ui';
import {
  InfiniteScrollTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import { statusBadgeClasses } from '~/components/ui/statusBadgeClasses';
import { formatDate, sellerLabel } from '~/utils';
import {
  RequestForQuoteStatus,
  type MyRequestForQuotesQuery,
} from '~/graphql/graphql';
import type { RequestForQuoteEmptyStateKey } from '~/pages/my-requests-for-quote/lib/requestForQuoteEmptyState';

export type MyRequestForQuoteRow =
  MyRequestForQuotesQuery['myRequestForQuotes']['content'][number];

type MyRequestForQuotesListProps = {
  requestForQuotes: MyRequestForQuoteRow[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  sentinelRef?: Ref<HTMLDivElement>;
  isLoadingMore?: boolean;
  hasMoreData?: boolean | null;
  totalCount?: number;
  emptyStateKey: RequestForQuoteEmptyStateKey;
  onShowArchivedRequests?: () => void;
};

function statusBadgeClassName(status: RequestForQuoteStatus): string {
  switch (status) {
    case RequestForQuoteStatus.AllQuotesReceived:
    case RequestForQuoteStatus.Fulfilled:
      return `${statusBadgeClasses('accepted')} whitespace-nowrap`;
    case RequestForQuoteStatus.PartialQuotesReceived:
      return `${statusBadgeClasses('info')} whitespace-nowrap`;
    case RequestForQuoteStatus.PendingSellerResponse:
      return `${statusBadgeClasses('pending')} whitespace-nowrap`;
    default:
      return `${statusBadgeClasses('neutral')} whitespace-nowrap`;
  }
}

function sellersSentTo(requestForQuote: MyRequestForQuoteRow): string {
  const sellers = new Map(
    requestForQuote.quotes.map(({ sellingOrganization: org }) => [
      org.id,
      sellerLabel(org),
    ])
  );
  return [...sellers.values()].join(', ');
}

export function MyRequestForQuotesList({
  requestForQuotes,
  isLoading,
  isError,
  onRetry,
  sentinelRef,
  isLoadingMore,
  hasMoreData,
  totalCount,
  emptyStateKey,
  onShowArchivedRequests,
}: Readonly<MyRequestForQuotesListProps>) {
  const { t } = useTranslation('my-requests-for-quote');
  const navigate = useNavigate();

  const columns: TableColumn[] = [
    {
      key: 'product',
      header: t('my_requests_for_quote.columns.product'),
      width: 16,
      stickTo: 'left',
    },
    {
      key: 'itemCount',
      header: t('my_requests_for_quote.columns.item_count'),
      width: 8,
    },
    {
      key: 'sellers',
      header: t('my_requests_for_quote.columns.sellers'),
      width: 16,
    },
    {
      key: 'createdBy',
      header: t('my_requests_for_quote.columns.created_by'),
      width: 12,
    },
    {
      key: 'status',
      header: t('my_requests_for_quote.columns.status'),
      width: 8,
    },
    {
      key: 'createdAt',
      header: t('my_requests_for_quote.columns.created_at'),
      width: 8,
    },
    {
      key: 'actions',
      header: t('my_requests_for_quote.columns.actions'),
      width: 8,
      justification: 'end',
      stickTo: 'right',
    },
  ];

  const rows: TableRow[] = requestForQuotes.map((requestForQuote) => ({
    id: { value: requestForQuote.id },
    product: {
      value:
        requestForQuoteProductName(requestForQuote) ??
        t('my_requests_for_quote.multiple_products'),
    },
    itemCount: {
      value: t('my_requests_for_quote.item_count', {
        count: requestForQuote.items.length,
      }),
    },
    sellers: { value: sellersSentTo(requestForQuote) || '—' },
    createdBy: { value: requestForQuote.createdByUserName ?? '—' },
    status: {
      value: (
        <span
          className={statusBadgeClassName(requestForQuote.status)}
          data-testid={`my-request-for-quote-status-${requestForQuote.id}`}
        >
          {t(`my_requests_for_quote.status.${requestForQuote.status}`)}
        </span>
      ),
    },
    createdAt: { value: formatDate(requestForQuote.createdAt) },
    actions: {
      value: (
        <button
          type="button"
          onClick={() => navigate(`/rfq/view/${requestForQuote.id}`)}
          data-testid={`my-request-for-quote-view-${requestForQuote.id}`}
          className="px-4 py-2 text-sm font-medium text-prim border border-prim rounded-lg hover:bg-emerald-50 transition-colors whitespace-nowrap"
        >
          {t('my_requests_for_quote.view')}
        </button>
      ),
    },
  }));

  return (
    <InfiniteScrollTable
      columns={columns}
      rows={rows}
      isLoading={isLoading}
      isError={isError}
      loadingName={t('my_requests_for_quote.title')}
      errorMessage={t('my_requests_for_quote.load_error')}
      errorTestId="my-requests-for-quote-error"
      onRetry={onRetry}
      emptyStateTitle={t(`${emptyStateKey}_title`)}
      emptyStateMessage={t(`${emptyStateKey}_message`)}
      emptyStateAction={
        onShowArchivedRequests ? (
          <Button
            variant="primary"
            onClick={onShowArchivedRequests}
            data-testid="my-requests-for-quote-show-archived"
          >
            {t('my_requests_for_quote.archived_hidden_cta')}
          </Button>
        ) : undefined
      }
      tableTestId="my-requests-for-quote-table"
      tableName={t('my_requests_for_quote.items')}
      loadingMoreLabel={t('my_requests_for_quote.loading_more')}
      noMoreItemsLabel={t('my_requests_for_quote.no_more_items')}
      totalCount={totalCount}
      isLoadingMore={isLoadingMore}
      hasMoreData={hasMoreData}
      sentinelRef={sentinelRef}
    />
  );
}
